import { create } from 'zustand';
import { persist } from 'zustand/middleware';

/** Environnement de déploiement (dev/beta/preprod/prod) — identique à ApiEnvId / EnvironmentId. */
export type DeployEnvId = 'dev' | 'beta' | 'preprod' | 'prod';

export const DEPLOY_ENVS: readonly DeployEnvId[] = ['dev', 'beta', 'preprod', 'prod'];

/** Garde de type : vrai ssi la valeur est un env de déploiement connu (ex. param d'URL). */
export function isDeployEnv(v: unknown): v is DeployEnvId {
  return v === 'dev' || v === 'beta' || v === 'preprod' || v === 'prod';
}

interface DeployEnvState {
  /** Env courant, PARTAGÉ entre les pages APIs / Environnement / Base de données / Resource pack. */
  env: DeployEnvId;
  setEnv: (env: DeployEnvId) => void;
}

/**
 * Le choix d'environnement SURVIT au rechargement de la page.
 *
 * <p>Sans persistance, le store repartait de 'prod' à chaque F5 : on consultait les APIs de dev,
 * on rafraîchissait, et on relisait la prod en croyant être resté sur dev. Le même piège existait
 * sur Base de données et Resource pack, qui lisent ce même store.</p>
 */
export const useDeployEnvStore = create<DeployEnvState>()(
  persist(
    (set) => ({
      env: 'prod',
      // Défensif : ignore toute valeur inconnue (un param d'URL casté ne doit jamais empoisonner le store partagé).
      setEnv: (env) => { if (isDeployEnv(env)) set({ env }); },
    }),
    {
      name: 'recubebe-deploy-env',
      partialize: (state) => ({ env: state.env }),
      // Même garde à la RÉHYDRATATION : un localStorage édité à la main, ou écrit par une version
      // antérieure, ne doit pas injecter un env que setEnv aurait refusé. On retombe sur 'prod',
      // l'environnement le moins surprenant quand on ne sait pas où on était.
      merge: (persisted, current) => {
        const saved = (persisted as Partial<DeployEnvState> | undefined)?.env;
        return { ...current, env: isDeployEnv(saved) ? saved : current.env };
      },
    },
  ),
);
